/** Preferencias de pantalla: copia en el dispositivo (al instante, sin conexión) y en la cuenta (para todos tus dispositivos). */
import { api } from './api-client.js';
import { normalizeHome, type HomeSection } from '../app/home-layout.js';
import { normalizeImportDefaults, type AccountImportDefaults } from '../app/import-model.js';

const KEYS = { home: 'pf-home-layout', tricount: 'pf-tricount-prefs', loads: 'pf-loads', importDefaults: 'pf-import-defaults' } as const;
type Key = keyof typeof KEYS;

function local<T>(key: Key): T | null {
  try { return JSON.parse(localStorage.getItem(KEYS[key]) ?? 'null') as T | null; } catch { return null; }
}

/** Guarda en el dispositivo y, salvo en la demostración, en la cuenta. Devuelve false si no se pudo subir. */
async function save(key: Key, value: unknown, demo: boolean): Promise<boolean> {
  try { localStorage.setItem(KEYS[key], JSON.stringify(value)); } catch { /* sin almacenamiento */ }
  if (demo) return true;
  try { await api.setPref(key, value); return true; } catch { return false; }
}

/** Lo que llega de la cuenta al abrir la app sustituye a la copia local. */
export function adoptServerPrefs(prefs: Record<string, unknown> | undefined) {
  for (const key of Object.keys(KEYS) as Key[]) {
    if (prefs?.[key] === undefined) continue;
    try { localStorage.setItem(KEYS[key], JSON.stringify(key === 'home' ? normalizeHome(prefs[key]) : prefs[key])); } catch { /* sin almacenamiento */ }
  }
}

export const homeLayout = (): HomeSection[] => normalizeHome(local('home'));
export const saveHomeLayout = (layout: HomeSection[], demo: boolean) => save('home', layout, demo);

/** Quién eres en Tricount (Raul o Pilar). */
export const tricountPayer = (): string => local<{ payer?: string }>('tricount')?.payer ?? '';
export const saveTricountPayer = (payer: string, demo: boolean) => save('tricount', { payer }, demo);

/** Cargas que se apuntan a mano (p. ej. Tickets): día del mes en que avisar y meses sin carga (1-12). */
export interface LoadReminder { account: string; day: number; skip: number[] }
export const loadReminders = (): LoadReminder[] => { const v = local<LoadReminder[]>('loads'); return Array.isArray(v) ? v : []; };
export const saveLoadReminders = (list: LoadReminder[], demo: boolean) => save('loads', list, demo);

/** Al importar, por cuenta: categorías que se marcan compartidas y las que además van a Tricount. Sin configurar, nada. */
export const importDefaultsList = (): AccountImportDefaults[] => normalizeImportDefaults(local('importDefaults'));
export const saveImportDefaults = (list: AccountImportDefaults[], demo: boolean) => save('importDefaults', normalizeImportDefaults(list), demo);
